/**
 * Popular Stocks Page
 * Ranked list of vehicles by stock number.
 * Data: /analytics/top-vehicles (grouped by VIN/stock).
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { List, Inbox, AlertCircle, RefreshCw, Info } from "lucide-react";
import { TimeRangeSelector } from "../components/dashboard";
import TableScroll from "../components/TableScroll";

const RowSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 animate-pulse">
        <div className="w-7 h-7 rounded-full bg-gray-200" />
        <div className="flex-1 h-4 bg-gray-200 rounded w-3/4" />
        <div className="w-16 h-4 bg-gray-200 rounded" />
      </div>
    ))}
  </div>
);

const PopularStocksPage = () => {
  const { apiFetch } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch(`/analytics/top-vehicles?days=${days}&metric=views&limit=50`);
      const list = (res.data?.items || []).filter(v => v.stock_number || v.vin);
      setItems(list);
    } catch (err) {
      console.error("Failed to fetch popular stocks:", err);
      setError(err.response?.data?.detail || "Failed to load stock data");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (error && !loading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-red-50 rounded-xl">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="text-lg font-medium text-red-600">Failed to load stock data</p>
          <p className="text-sm text-red-500 mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
            data-testid="popular-stocks-retry"
          >
            <RefreshCw className="w-4 h-4" /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full" data-testid="popular-stocks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Popular Stocks</h1>
          <p className="text-gray-500 text-sm mt-0.5">Most-viewed vehicles ranked by stock number</p>
        </div>
        <TimeRangeSelector value={days} onChange={setDays} disabled={loading} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <List className="w-5 h-5 text-indigo-500" />
              <CardTitle className="text-lg">Stock Rankings</CardTitle>
            </div>
            <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
              Last {days} days
            </span>
          </div>
          <CardDescription>Individual vehicles by stock number</CardDescription>
          {/* iter89 — Issue 24: explain the inventory-feed concept so
              dealers understand what to do if rows show "Not found in
              inventory feed". VINs are still auto-decoded via NHTSA so
              this affects only stock numbers the embed couldn't tie to
              a VIN at all. */}
          <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-md text-xs text-blue-900" data-testid="inventory-feed-help">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-blue-500" />
            <div>
              <strong>How vehicle details are matched:</strong> Year/Make/Model are first read from your inventory feed events, then backfilled from verified leads, and finally decoded from the VIN itself via the public NHTSA database. Rows showing <em>&quot;Not found in inventory feed&quot;</em> mean the embed captured a stock number that doesn&apos;t include a decodable VIN — connect your inventory feed in Settings &rarr; Inventory to fix these.
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <RowSkeleton />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Inbox className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-base font-medium text-gray-500 mb-1">No stock data yet</p>
              <p className="text-sm text-gray-400 max-w-xs">Once visitors view vehicles on your site, the most-viewed stock numbers will appear here.</p>
            </div>
          ) : (
            <TableScroll testId="popular-stocks-scroll">
              <table className="w-full text-sm" data-testid="popular-stocks-table">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase text-gray-500 tracking-wide">
                    <th className="py-3 px-3 w-16">Rank</th>
                    <th className="py-3 px-3">Stock #</th>
                    <th className="py-3 px-3">Vehicle</th>
                    <th className="py-3 px-3 text-right">Views</th>
                    <th className="py-3 px-3 text-right">Unlocks</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((v, idx) => {
                    // iter89 — Issue 24: differentiate truly-missing vehicle
                    // data from data that just needs an inventory-feed sync.
                    // The backend signals this via `enriched_from`:
                    //   "embed"      — captured live by the embed
                    //   "lead"       — backfilled from a verified unlock lead
                    //   "vin_decode" — decoded from the VIN via NHTSA vPIC
                    //   "none"       — every source failed
                    const ymmt = [v.year, v.make, v.model, v.trim].filter(Boolean).join(" ");
                    let vehicleLabel;
                    if (ymmt) {
                      vehicleLabel = (
                        <span title={`Source: ${v.enriched_from || "feed"}`}>{ymmt}</span>
                      );
                    } else if (v.vin && v.enriched_from === "none") {
                      vehicleLabel = (
                        <span
                          className="text-gray-400 italic"
                          title="VIN couldn't be decoded via the public NHTSA database. Connect your inventory feed for richer metadata."
                        >
                          Not found in inventory feed
                        </span>
                      );
                    } else {
                      vehicleLabel = (
                        <span className="text-gray-400 italic" title="No VIN was captured for this stock number — connect your inventory feed in Settings → Inventory to fix this.">
                          Not found in inventory feed
                        </span>
                      );
                    }
                    const stockLabel = v.stock_number
                      ? v.stock_number
                      : (v.vin ? v.vin : <span className="text-gray-400">—</span>);
                    return (
                      <tr key={`${v.vin || v.stock_number || idx}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`popular-stock-row-${idx}`}>
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${idx < 3 ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="py-3 px-3 font-mono text-gray-700">{stockLabel}</td>
                        <td className="py-3 px-3 font-medium text-gray-900">{vehicleLabel}</td>
                        <td className="py-3 px-3 text-right font-semibold tabular-nums">{(v.views_count || 0).toLocaleString()}</td>
                        <td className="py-3 px-3 text-right tabular-nums text-gray-700">{(v.unlocks_count || 0).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PopularStocksPage;
